import { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { CTA, Footer } from './App';
import './BlogPage.css';

/* ============================================
   SCROLL ANIMATION HOOK
   ============================================ */
function useScrollAnimation() {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => { if (entry.isIntersecting) setIsVisible(true); }, { threshold: 0.1, rootMargin: '0px 0px -50px 0px' });
    const current = ref.current;
    if (current) observer.observe(current);
    return () => { if (current) observer.unobserve(current); };
  }, []);
  return [ref, isVisible];
}

function AnimatedSection({ children, className = '', delay = 0 }) {
  const [ref, isVisible] = useScrollAnimation();
  return (
    <div ref={ref} className={`fade-in ${isVisible ? 'visible' : ''} ${className}`} style={{ transitionDelay: `${delay}s` }}>
      {children}
    </div>
  );
}

/* ============================================
   ICONS
   ============================================ */
const Icons = {
  ArrowLeft: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M19 12H5"/><path d="m12 19-7-7 7-7"/>
    </svg>
  ),
  Search: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/>
    </svg>
  ),
  ArrowRight: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 12h14"/><path d="m12 5 7 7-7 7"/>
    </svg>
  ),
};

/* ============================================
   BLOG DATA
   ============================================ */
const blogPosts = [
  {
    slug: 'honoring-those-who-served',
    title: 'Honoring Those Who Served: Why Veteran Support Matters',
    excerpt: 'Every day, thousands of veterans across America face challenges ranging from homelessness to mental health struggles. Learn why community-based support is critical.',
    category: 'Veteran Support',
    date: 'March 2026',
    readTime: '5 min read',
    image: '/images/serving-those-who-served.webp',
    content: 'Every day, thousands of veterans across America face challenges ranging from homelessness to mental health struggles. After dedicating years of service to our country, many return to civilian life only to encounter barriers to housing, employment, and healthcare.\n\nAt Mission Critical Support Group, we believe that those who served deserve more than gratitude — they deserve action. Our programs provide direct support through housing assistance, meals, education, and financial literacy training.\n\n**Why Community Support Matters**\n\nVeterans often face unique challenges that require specialized understanding and resources. Community-based organizations like MCSG bridge the gap between government services and individual needs, providing personalized support that helps veterans rebuild their lives.\n\n**How You Can Help**\n\nWhether through donation, volunteering, or simply spreading awareness, every action counts in our mission to restore hope and rebuild lives for those who have served.',
  },
  {
    slug: 'breaking-the-cycle-of-domestic-violence',
    title: 'Breaking the Cycle: Supporting Survivors of Domestic Violence',
    excerpt: 'Domestic violence affects millions of people each year. Discover how safe shelter, advocacy, and life-changing support services can help survivors rebuild.',
    category: 'Advocacy',
    date: 'February 2026',
    readTime: '6 min read',
    image: '/images/breaking-the-cycle.webp',
    content: 'Domestic violence is a pervasive issue that affects people of all backgrounds, ages, and communities. Each year, approximately 10 million Americans experience domestic violence, with women and children being disproportionately impacted.\n\nAt Mission Critical Support Group, we are committed to breaking this cycle through comprehensive support services.\n\n**Safe Shelter**\n\nThe first step toward healing is safety. We work to connect survivors with safe housing options where they can begin the process of rebuilding without fear.\n\n**Advocacy & Legal Support**\n\nNavigating the legal system can be overwhelming for survivors. Our advocacy programs help connect individuals with the resources and representation they need.\n\n**Life-Changing Support**\n\nFrom counseling to financial literacy, our programs address the whole person, helping survivors develop the skills and confidence to build independent, fulfilling lives.',
  },
  {
    slug: 'financial-literacy-pathway-to-independence',
    title: 'Financial Literacy: A Pathway to Independence',
    excerpt: 'Financial literacy is one of the most powerful tools for building long-term stability. Learn about our approach to empowering individuals through financial education.',
    category: 'Education',
    date: 'January 2026',
    readTime: '4 min read',
    image: '/images/financial-literacy.webp',
    content: 'Financial literacy is one of the most powerful tools for building long-term stability and independence. For veterans transitioning to civilian life and survivors rebuilding after crisis, understanding money management is essential.\n\nOur financial literacy programs cover:\n\n* Budgeting and expense tracking\n* Understanding credit and building credit scores\n* Savings strategies and emergency funds\n* Employment readiness and income planning\n* Avoiding predatory lending and scams\n\n**Real Impact**\n\nParticipants in financial literacy programs are significantly more likely to achieve housing stability and maintain employment. By investing in financial education, we are investing in futures.\n\nIf you or someone you know could benefit from these programs, contact us to learn more about enrollment opportunities.',
  },
  {
    slug: 'tech-education-bridging-digital-divide',
    title: 'Tech Education: Bridging the Digital Divide',
    excerpt: 'In today\'s world, digital literacy is essential. Through our new partnership with Creator Terminal, we\'re expanding access to digital media education, coding, and professional development for veterans and community members.',
    category: 'Tech Education',
    date: 'November 2026',
    readTime: '8 min read',
    image: '/images/tech-education.webp',
    content: 'In an increasingly digital world, technology skills are no longer optional. They are essential for employment, communication, and daily life. Yet many veterans and underserved community members lack access to technology and training.\n\nMission Critical Support Group addresses this gap through our Tech Education & Literacy programs, and we are excited to announce a new collaboration that takes this mission even further.\n\n**Partnering with Creator Terminal**\n\nWe are proud to announce our collaboration with [Creator Terminal](https://creatorterminal.com), a fully recognized 501(c)(3) nonprofit dedicated to empowering people with accessible education and resources in digital media and professional development. Based in the Katy and greater Houston area, [Creator Terminal](https://creatorterminal.com) shares our commitment to bridging the gap between potential and opportunity.\n\nThrough this partnership, MCSG participants will gain access to an expanded range of programs and resources that go well beyond basic digital literacy.\n\n**What This Partnership Brings**\n\nTogether with Creator Terminal, we are expanding our offerings to include:\n\n*Digital Media Creation*\n* Video production and filmmaking\n* Photography and visual storytelling\n* Audio recording, podcasting, and audio production\n* Graphic design, branding, and digital art\n\n*Tech and Development*\n* Coding fundamentals and web development\n* Automation and practical creator tech skills\n* Software skills for workplace readiness\n\n*3D and Interactive Media*\n* 3D modeling and animation\n* Game design fundamentals\n* Real-time tools and pipelines using industry-standard software\n\n*Business Operations and Growth*\n* Business administration and logistics\n* Scaling strategies, workflows, and project management\n* Pricing, quoting, and professional development skills\n\n**Community at the Core**\n\nCreator Terminal brings a strong community-first approach, offering local networking, meetups, workshops, and collaboration opportunities. Their vision to build a large-scale community facility for hands-on education aligns perfectly with MCSG\'s mission to restore hope and rebuild lives.\n\nVeterans, survivors, and community members served by MCSG will be able to connect with mentors, find creative collaborators, and access real-world opportunities through Creator Terminal\'s growing network.\n\n**Why It Matters**\n\nDigital literacy opens doors to employment, education, and connection. By combining MCSG\'s dedication to serving veterans and underserved communities with Creator Terminal\'s deep expertise in digital media education, we are creating a pathway that goes beyond basic skills. We are helping people build careers, tell their stories, and grow both creatively and professionally.\n\nTo learn more, visit [Creator Terminal](https://creatorterminal.com) or [reach out to us](https://missioncriticalsupportgroup.com/contact) to learn how you can get involved.',
  },
];

/* ============================================
   BLOG PAGE
   ============================================ */
function BlogPage() {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const categories = ['All', ...new Set(blogPosts.map(p => p.category))];

  const filtered = blogPosts.filter(post => {
    const matchesSearch = post.title.toLowerCase().includes(searchTerm.toLowerCase()) || post.excerpt.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'All' || post.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="blog-page">
      <Helmet>
        <title>Impact Stories & News | Mission Critical Support Group - Katy, TX</title>
        <meta name="description" content="Read stories of impact from Mission Critical Support Group in Katy, TX. Learn about veteran support programs, domestic violence advocacy, financial literacy, tech education, and community impact in Fort Bend County." />
        <link rel="canonical" href="https://www.missioncriticalsupportgroup.com/blog" />

        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.missioncriticalsupportgroup.com/blog" />
        <meta property="og:title" content="Impact Stories & News | Mission Critical Support Group" />
        <meta property="og:description" content="Stories of hope and resilience from Mission Critical Support Group. See how we support veterans and domestic violence survivors in Katy, TX." />
        <meta property="og:site_name" content="Mission Critical Support Group" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Impact Stories | Mission Critical Support Group" />
        <meta name="twitter:description" content="Stories of hope and resilience from MCSG — supporting veterans and survivors in Katy, TX." />
      </Helmet>

      {/* Navigation */}
      <nav className="blog-nav">
        <div className="blog-nav-inner">
          <a href="/" className="blog-nav-back"><Icons.ArrowLeft /><span>Back to Home</span></a>
        </div>
      </nav>

      {/* Hero */}
      <section className="blog-hero">
        <AnimatedSection>
          <div className="blog-hero-label">Impact Stories</div>
          <h1 className="blog-hero-title">Stories of Hope & Resilience</h1>
          <p className="blog-hero-description">
            Discover how Mission Critical Support Group is making a difference in the lives
            of veterans and survivors of domestic violence across Katy, TX and the greater Houston community.
          </p>
        </AnimatedSection>
      </section>

      {/* Search & Filter */}
      <section className="blog-controls">
        <div className="blog-controls-inner">
          <div className="blog-search">
            <Icons.Search />
            <input type="text" placeholder="Search stories..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
          </div>
          <div className="blog-categories">
            {categories.map(cat => (
              <button key={cat} className={`blog-cat-btn ${selectedCategory === cat ? 'active' : ''}`} onClick={() => setSelectedCategory(cat)}>
                {cat}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Blog Grid */}
      <section className="blog-grid-section">
        <div className="blog-grid">
          {filtered.map((post, index) => (
            <AnimatedSection key={post.slug} delay={index * 0.1}>
              <a href={`/blog/${post.slug}`} className="blog-card">
                <div className="blog-card-image" style={{ background: post.image ? `url(${post.image}) center/cover no-repeat` : 'linear-gradient(135deg, #1B2A4A 0%, #2a3f6a 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', height: '200px' }}>
                  {!post.image && <span style={{ color: '#C5A55A', fontSize: '2rem', fontWeight: 800, fontFamily: 'var(--font-display)' }}>MCSG</span>}
                </div>
                <div className="blog-card-content">
                  <div className="blog-card-meta">
                    <span className="blog-card-category">{post.category}</span>
                    <span>{post.date}</span>
                    <span>{post.readTime}</span>
                  </div>
                  <h3>{post.title}</h3>
                  <p>{post.excerpt}</p>
                  <span className="blog-card-link">Read More <Icons.ArrowRight /></span>
                </div>
              </a>
            </AnimatedSection>
          ))}
        </div>
        {filtered.length === 0 && (
          <p style={{ textAlign: 'center', color: 'var(--color-text-muted)', padding: '3rem 0' }}>No stories found matching your search.</p>
        )}
      </section>

      <CTA />
      <Footer />
    </div>
  );
}

export { blogPosts };
export default BlogPage;
